import { applyRefinesToSearchParams } from '@/components/eataly-plp-filters/lib/refine-url';
import { CONTENT_FOLDER_REFINEMENT_ATTRIBUTE_ID, CONTENT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

type ContentFolderFilterTarget = {
    id: string;
    path: string;
};

type ContentListingFolder = {
    folderId: string;
    path: string;
};

function contentFolderRefinePair(folderId: string): string {
    return `${CONTENT_FOLDER_REFINEMENT_ATTRIBUTE_ID}=${folderId}`;
}

/** Child-folder `fdid` values from the refine list, in URL order, without duplicates. */
export function selectedContentFolderFilterIds(
    refine: readonly string[],
    allowedFolderIds: ReadonlySet<string>
): string[] {
    const selectedFolderIds: string[] = [];
    const seen = new Set<string>();

    for (const value of refine) {
        const separatorIndex = value.indexOf('=');
        if (separatorIndex <= 0 || value.slice(0, separatorIndex) !== CONTENT_FOLDER_REFINEMENT_ATTRIBUTE_ID) {
            continue;
        }
        const folderId = value.slice(separatorIndex + 1);
        if (!allowedFolderIds.has(folderId) || seen.has(folderId)) {
            continue;
        }
        seen.add(folderId);
        selectedFolderIds.push(folderId);
    }

    return selectedFolderIds;
}

/** Adds or removes one child folder. Other selected folders stay in the refine list. */
export function toggleContentFolderFilter(currentRefines: readonly string[], folderId: string): string[] {
    const pair = contentFolderRefinePair(folderId);
    if (currentRefines.includes(pair)) {
        return currentRefines.filter((refine) => refine !== pair);
    }
    return [...currentRefines, pair];
}

export function searchWithContentFolderFilter(search: string, folderId: string): string {
    const params = new URLSearchParams(search);
    const nextRefines = toggleContentFolderFilter(params.getAll(CONTENT_SEARCH_QUERY_PARAMS.REFINE), folderId);
    applyRefinesToSearchParams(params, nextRefines);
    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
}

/**
 * Child-folder filters narrow the current listing only when the folder flag is on. With the flag
 * off, or with none selected, the page folder is the search scope. An `fdid` that is not a child
 * of this listing is ignored.
 */
export function resolveContentListingSearchFolders(
    folder: ContentListingFolder,
    subCategoryLinks: readonly ContentFolderFilterTarget[],
    refine: readonly string[],
    showCategoryFilter: boolean
): ContentListingFolder[] {
    if (!showCategoryFilter) {
        return [{ folderId: folder.folderId, path: folder.path }];
    }

    const selectedFolderIds = selectedContentFolderFilterIds(refine, new Set(subCategoryLinks.map((link) => link.id)));
    if (selectedFolderIds.length === 0) {
        return [{ folderId: folder.folderId, path: folder.path }];
    }

    return selectedFolderIds.map((folderId) => {
        const selected = subCategoryLinks.find((link) => link.id === folderId);
        return {
            folderId,
            path: selected?.path ?? folder.path,
        };
    });
}
